import React, { useEffect } from 'react';

const InterpreterPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Interpreter Pattern - Behavioral Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Interpreter Pattern - define grammar for a language and interpret sentences. Learn with mathematical expressions and query language examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Interpreter Pattern - define grammar for a language and interpret sentences. Learn with mathematical expressions and query language examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'interpreter pattern, design patterns, behavioral patterns, grammar, language parsing, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'interpreter pattern, design patterns, behavioral patterns, grammar, language parsing, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Interpreter Pattern</h1>
        <p style={styles.subtitle}>
          Behavioral Design Pattern - Define grammar and interpret language sentences
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Interpreter pattern defines a representation for a language's grammar and provides 
            an interpreter to deal with this grammar. It's used to evaluate sentences in a language 
            by representing grammar rules as classes and building an abstract syntax tree.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            You need to interpret expressions written in a specific language or notation. Rather 
            than building a complex parser, you want to define grammar rules as classes and build 
            an interpreter that can evaluate expressions based on these rules.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: Mathematical Expression Evaluator</h2>
          <div style={styles.codeBlock}>
{`// Context class to hold variables
class Context {
    private Map<String, Integer> variables = new HashMap<>();
    
    public void setVariable(String name, int value) {
        variables.put(name, value);
    }
    
    public int getVariable(String name) {
        return variables.getOrDefault(name, 0);
    }
    
    public boolean hasVariable(String name) {
        return variables.containsKey(name);
    }
    
    public void showVariables() {
        System.out.println("Variables: " + variables);
    }
}

// Abstract expression interface
interface Expression {
    int interpret(Context context);
}

// Terminal expressions (leaf nodes)
class NumberExpression implements Expression {
    private int number;
    
    public NumberExpression(int number) {
        this.number = number;
    }
    
    @Override
    public int interpret(Context context) {
        return number;
    }
    
    @Override
    public String toString() {
        return String.valueOf(number);
    }
}

class VariableExpression implements Expression {
    private String variableName;
    
    public VariableExpression(String variableName) {
        this.variableName = variableName;
    }
    
    @Override
    public int interpret(Context context) {
        return context.getVariable(variableName);
    }
    
    @Override
    public String toString() {
        return variableName;
    }
}

// Non-terminal expressions (composite nodes)
class AddExpression implements Expression {
    private Expression left;
    private Expression right;
    
    public AddExpression(Expression left, Expression right) {
        this.left = left;
        this.right = right;
    }
    
    @Override
    public int interpret(Context context) {
        return left.interpret(context) + right.interpret(context);
    }
    
    @Override
    public String toString() {
        return "(" + left + " + " + right + ")";
    }
}

class SubtractExpression implements Expression {
    private Expression left;
    private Expression right;
    
    public SubtractExpression(Expression left, Expression right) {
        this.left = left;
        this.right = right;
    }
    
    @Override
    public int interpret(Context context) {
        return left.interpret(context) - right.interpret(context);
    }
    
    @Override
    public String toString() {
        return "(" + left + " - " + right + ")";
    }
}

class MultiplyExpression implements Expression {
    private Expression left;
    private Expression right;
    
    public MultiplyExpression(Expression left, Expression right) {
        this.left = left;
        this.right = right;
    }
    
    @Override
    public int interpret(Context context) {
        return left.interpret(context) * right.interpret(context);
    }
    
    @Override
    public String toString() {
        return "(" + left + " * " + right + ")";
    }
}

class DivideExpression implements Expression {
    private Expression left;
    private Expression right;
    
    public DivideExpression(Expression left, Expression right) {
        this.left = left;
        this.right = right;
    }
    
    @Override
    public int interpret(Context context) {
        int rightValue = right.interpret(context);
        if (rightValue == 0) {
            throw new ArithmeticException("Division by zero");
        }
        return left.interpret(context) / rightValue;
    }
    
    @Override
    public String toString() {
        return "(" + left + " / " + right + ")";
    }
}

// Parser to build expression tree from string
class ExpressionParser {
    
    public static Expression parse(String expression) {
        // Simple recursive descent parser
        // This is a simplified version - real parsers are more complex
        return parseExpression(expression.replaceAll("\\\\s+", ""), new int[]{0});
    }
    
    private static Expression parseExpression(String expr, int[] pos) {
        Expression left = parseTerm(expr, pos);
        
        while (pos[0] < expr.length()) {
            char op = expr.charAt(pos[0]);
            if (op == '+' || op == '-') {
                pos[0]++; // consume operator
                Expression right = parseTerm(expr, pos);
                if (op == '+') {
                    left = new AddExpression(left, right);
                } else {
                    left = new SubtractExpression(left, right);
                }
            } else {
                break;
            }
        }
        
        return left;
    }
    
    private static Expression parseTerm(String expr, int[] pos) {
        Expression left = parseFactor(expr, pos);
        
        while (pos[0] < expr.length()) {
            char op = expr.charAt(pos[0]);
            if (op == '*' || op == '/') {
                pos[0]++; // consume operator
                Expression right = parseFactor(expr, pos);
                if (op == '*') {
                    left = new MultiplyExpression(left, right);
                } else {
                    left = new DivideExpression(left, right);
                }
            } else {
                break;
            }
        }
        
        return left;
    }
    
    private static Expression parseFactor(String expr, int[] pos) {
        if (pos[0] >= expr.length()) {
            throw new IllegalArgumentException("Unexpected end of expression");
        }
        
        char c = expr.charAt(pos[0]);
        
        if (c == '(') {
            pos[0]++; // consume '('
            Expression result = parseExpression(expr, pos);
            if (pos[0] >= expr.length() || expr.charAt(pos[0]) != ')') {
                throw new IllegalArgumentException("Missing closing parenthesis");
            }
            pos[0]++; // consume ')'
            return result;
        } else if (Character.isDigit(c)) {
            return parseNumber(expr, pos);
        } else if (Character.isLetter(c)) {
            return parseVariable(expr, pos);
        } else {
            throw new IllegalArgumentException("Unexpected character: " + c);
        }
    }
    
    private static Expression parseNumber(String expr, int[] pos) {
        int start = pos[0];
        while (pos[0] < expr.length() && Character.isDigit(expr.charAt(pos[0]))) {
            pos[0]++;
        }
        int number = Integer.parseInt(expr.substring(start, pos[0]));
        return new NumberExpression(number);
    }
    
    private static Expression parseVariable(String expr, int[] pos) {
        int start = pos[0];
        while (pos[0] < expr.length() && Character.isLetterOrDigit(expr.charAt(pos[0]))) {
            pos[0]++;
        }
        String variable = expr.substring(start, pos[0]);
        return new VariableExpression(variable);
    }
}

// Calculator class using interpreter
class MathCalculator {
    private Context context;
    
    public MathCalculator() {
        this.context = new Context();
    }
    
    public void setVariable(String name, int value) {
        context.setVariable(name, value);
        System.out.println("Set " + name + " = " + value);
    }
    
    public int evaluate(String expression) {
        try {
            Expression expr = ExpressionParser.parse(expression);
            System.out.println("Parsing: " + expression);
            System.out.println("Tree: " + expr);
            
            int result = expr.interpret(context);
            System.out.println("Result: " + result);
            return result;
        } catch (Exception e) {
            System.err.println("Error evaluating '" + expression + "': " + e.getMessage());
            return 0;
        }
    }
    
    public void showVariables() {
        context.showVariables();
    }
}

// Usage
public class InterpreterDemo {
    public static void main(String[] args) {
        MathCalculator calculator = new MathCalculator();
        
        System.out.println("=== Mathematical Expression Interpreter ===\\n");
        
        // Set variables
        calculator.setVariable("x", 10);
        calculator.setVariable("y", 5);
        calculator.setVariable("z", 2);
        
        System.out.println();
        calculator.showVariables();
        
        System.out.println("\\n=== Evaluating Expressions ===\\n");
        
        // Test expressions
        String[] expressions = {
            "5 + 3",
            "10 - 4",
            "6 * 7",
            "20 / 4",
            "x + y",
            "x * y",
            "x + y * z",
            "(x + y) * z",
            "x * y + z",
            "x + y - z * 2"
        };
        
        for (String expr : expressions) {
            System.out.println("Expression: " + expr);
            calculator.evaluate(expr);
            System.out.println();
        }
        
        // Test with different variable values
        System.out.println("=== Changing Variables ===\\n");
        calculator.setVariable("x", 20);
        calculator.setVariable("y", 3);
        
        System.out.println("\\nRe-evaluating: x + y * z");
        calculator.evaluate("x + y * z");
        
        System.out.println("\\nRe-evaluating: (x + y) * z");
        calculator.evaluate("(x + y) * z");
        
        // Test error handling
        System.out.println("\\n=== Error Handling ===\\n");
        calculator.evaluate("10 / 0");  // Division by zero
        calculator.evaluate("5 + ");    // Incomplete expression
        calculator.evaluate("(5 + 3");  // Missing parenthesis
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Grammar Representation:</strong> Each grammar rule is represented by a class</li>
            <li style={styles.listItem}><strong>Extensibility:</strong> Easy to add new grammar rules and operations</li>
            <li style={styles.listItem}><strong>Reusability:</strong> Expressions can be reused and combined</li>
            <li style={styles.listItem}><strong>Maintainability:</strong> Grammar changes are localized to specific classes</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When you have a simple grammar to interpret</li>
            <li style={styles.listItem}>When efficiency is not a primary concern</li>
            <li style={styles.listItem}>When you need to interpret expressions multiple times</li>
            <li style={styles.listItem}>When grammar rules are relatively stable</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Interpreter pattern is useful for creating domain-specific languages and expression 
            evaluators. While not commonly used for complex languages (due to performance concerns), 
            it's excellent for simple grammars like mathematical expressions, configuration languages, 
            and rule engines.
          </p>
        </section>
      </main>
    </article>
  );
};

export default InterpreterPattern;
